/**
 * The dashboard's app-wide graph display defaults.
 *
 * Every graph surface in the product — the transaction detail page's Semantic
 * and Flow tabs, a cross-chain saga, an investigation's case graph — is built on
 * the same `GraphScene` contract, so which panels it shows and how it resolves
 * icons and brand accents should be one decision, made here, rather than a set
 * of props each page remembers to pass. A surface that needs to differ still
 * overrides locally; see `GraphDefaultsProvider`.
 *
 * Imported from `@authe/tx-graph/defaults`, NOT the package barrel: this mounts
 * at the app root, and the barrel would pull React Flow and the layout engines
 * into the eager root chunk, defeating the lazily-loaded graph panels.
 */
import * as React from "react";
import {
  GraphDefaultsProvider,
  type GraphDefaults,
  graphShareStateHasContent,
  type GraphViewsSlotProps,
} from "@authe/tx-graph/defaults";
import { accentClasses, brandForGraphLabel, resolveBrand } from "@authe/protocol-ui";
import { assetCdnBase } from "@/config";
import { useBrandOverrides } from "./brand";

/**
 * The saved-views bar, loaded with the graph rather than with the app.
 *
 * It pulls `@authe/saved-views` and the tx-graph barrel, and this module mounts
 * at the root — the same reason the provider itself is imported from
 * `@authe/tx-graph/defaults`. The slot only renders inside a graph, which is
 * already lazily loaded, so the chunk arrives with the surface that needs it.
 */
const LazyGraphSavedViewsBar = React.lazy(async () => {
  const mod = await import("@/features/graphs/GraphSavedViewsBar");
  return { default: mod.GraphSavedViewsBar };
});

function GraphViewsSlot({ graphId, state, onApplyState }: GraphViewsSlotProps): React.ReactElement {
  return (
    <React.Suspense fallback={null}>
      <LazyGraphSavedViewsBar
        graphId={graphId}
        currentGraph={state}
        onApplyGraph={onApplyState}
        // The same "is there a graph here" rule the graphs page asks — it used
        // to look only at `scene.nodes` while that page looked only at `nodes`,
        // so the two disagreed about the shapes the other one produces.
        saveDisabled={!graphShareStateHasContent(state)}
      />
    </React.Suspense>
  );
}

export function DashboardGraphDefaults({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  // Default system brand + tenant/user overrides (user > tenant > default). No
  // chain is pinned here: a graph can span chains, so a per-chain accent override
  // is the individual surface's to apply.
  const brandOverrides = useBrandOverrides();

  const value = React.useMemo<GraphDefaults>(
    () => ({
      // The seeded CDN the backend's `cdn_key` media refs (chain / token /
      // protocol / bridge logos) resolve against.
      cdnBase: assetCdnBase,
      // Map an off-chain node's `attributes.protocol` (the backend
      // `SceneOffchainSpec.label`, e.g. "Chainlink CCIP") to its brand accent, so
      // a Wormhole hop reads violet and a CCIP hop blue on every surface.
      brandAccent: (protocol: string) => {
        const base = brandForGraphLabel(protocol);
        return base ? accentClasses(resolveBrand(base, brandOverrides)).border : undefined;
      },
      // One rectangle per blockchain, everywhere — the same grouping the backend
      // applies when it builds a scene, now stated once for the graphs the client
      // assembles itself (a case file) as well. Users regroup per surface from
      // the settings gear.
      grouping: { dimensions: ["chain"] },
      // Save / apply / share, on every graph rather than the two surfaces that
      // happened to mount a bar.
      renderViewsBar: (props) => <GraphViewsSlot {...props} />,
    }),
    [brandOverrides],
  );

  return <GraphDefaultsProvider value={value}>{children}</GraphDefaultsProvider>;
}
